import { useContext, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import classNames from 'classnames';
import { Button, ButtonSize, ButtonType } from '@nx-frontend/shared/ui/button';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { IncDecCounter } from '@nx-frontend/shared/ui/inc-dec-counter';
import { TippyTooltip } from '@nx-frontend/shared/ui/tooltip';
import { webnotesThemeColors, webnotesThemeStyle } from '@nx-frontend/shared/themes/webnotes';
import { UiTooltipPosition } from '@nx-frontend/shared/types';
import { PaywallAddonCardPropsWithLoc } from '@nx-frontend/shared/types/paywall-types';
import { AddonContext } from './addon-context';
import { PaywallContext } from './paywall-context';

export const PaywallAddonCard = (props: PaywallAddonCardPropsWithLoc) => {
  const {
    title,
    titleLocKey,
    price,
    perLabel,
    perLabelLocKey,
    description,
    descriptionLocKey,
    label,
    labelLocKey,
    showCounter,
    showAddButton,
    selectable,
    selected,
    disabled,
    value = 0,
    onCounterChange = () => console.log('onCounterChange'),
    onAddButtonClick = () => console.log('onAddButtonClick'),
    min = 0,
  } = props;

  const t = useTranslations();
  const displayedTitle = titleLocKey ? t(titleLocKey) : title;
  const displayedPerLabel = perLabelLocKey ? t(perLabelLocKey) : perLabel;
  const displayedDescription = descriptionLocKey ? t(descriptionLocKey) : description;
  const displayedLabel = labelLocKey ? t(labelLocKey) : label;

  const paywallCtx = useContext(PaywallContext);
  const addonCtx = useContext(AddonContext);

  const discount = useMemo(() => {
    const action = addonCtx.action || paywallCtx.action;
    if (action && action.discount) {
      return action.discount;
    }
    return 0;
  }, [addonCtx, paywallCtx]);

  const styles = {
    textPriceColor: disabled ? 'text-neutral-400' : discount ? 'text-rose-500 font-semibold' : 'text-neutral-800',
    textDiscountOriginalPriceColor: disabled ? 'text-neutral-400' : 'text-neutral-500',
    textUsdSize: discount ? 'text-2xl' : 'text-base',
  };

  return (
    <div
      className={classNames('paywall__addon-card py-4 px-5 pr-4 border rounded-lg relative duration-200 bg-white', {
        'hover:border-cyan-700': !disabled && selectable,
        '!border-cyan-700 shadow-[0_0_0_3px_#E0F2FE]': !disabled && selected,
        'bg-zinc-50 pointer-events-none opacity-[0.64]': disabled,
      })}
    >
      {displayedTitle ? (
        <div className="flex justify-between mb-4">
          <div
            className={classNames('text-lg font-semibold leading-6', {
              'text-neutral-400': disabled,
            })}
          >
            {displayedTitle}
          </div>
          {displayedLabel ? (
            <TippyTooltip message={displayedLabel} position={UiTooltipPosition.BOTTOM}>
              <div className="w-5 h-5">
                <Icon icon={IconsMap.INFO} className="w-full text-neutral-500 cursor-pointer" />
              </div>
            </TippyTooltip>
          ) : null}
        </div>
      ) : null}
      <div className={classNames(styles.textPriceColor, 'mb-2')}>
        <span className={classNames('font-semibold', styles.textUsdSize)}>$</span>
        <span className="text-2xl font-semibold">{!discount ? price : (price * (1 - discount / 100)).toFixed(1)}</span>
        {discount ? (
          <span
            className={classNames(
              'text-base font-normal inline-block mx-1 line-through',
              styles.textDiscountOriginalPriceColor
            )}
          >
            ${price}
          </span>
        ) : null}
        <span
          className={classNames('text-xs', {
            'text-neutral-400': disabled,
            'text-neutral-500': !disabled,
          })}
        >
          /{displayedPerLabel}
        </span>
      </div>
      <div className="flex justify-between items-center">
        <div className="flex-1 max-w-[220px]">
          <span
            className={classNames('text-xs p-1.5 inline-block rounded bg-gray-100', {
              'text-neutral-400': disabled,
              'text-neutral-800': !disabled,
            })}
          >
            {displayedDescription}
          </span>
        </div>
        {showCounter ? <IncDecCounter value={value} onChange={onCounterChange} min={min} /> : null}
        {showAddButton ? (
          <Button
            type={ButtonType.SECONDARY}
            size={ButtonSize.MD}
            onClick={() => onAddButtonClick()}
            themeColors={webnotesThemeColors}
            themeStyle={webnotesThemeStyle}
            className={classNames('h-[36px] w-auto !px-4 !rounded !border-cyan-900', {
              'border-neutral-200 !text-neutral-300 bg-zinc-50': disabled,
              '!text-cyan-900': !disabled,
            })}
          >
            {!selected ? t('features.paywall.add_button') : t('features.paywall.remove_button')}
          </Button>
        ) : null}
      </div>
    </div>
  );
};
